<template>
	<div class="properties-panel">
		<template v-if="block">
			<div class="pa-3 pb-2 d-flex align-center">
				<v-icon color="primary" class="mr-2">{{ blockIcon }}</v-icon>
				<p class="subtitle-1 font-weight-medium mb-0">{{ blockLabel }}</p>
			</div>

			<v-divider></v-divider>

			<div class="properties-panel__scroll">
				<!-- key: pannelli nuovi a ogni blocco, vedi il watcher su block.id -->
				<v-expansion-panels :key="block.id" v-model="openPanels" multiple flat accordion>

					<!-- Block-specific settings -->
					<v-expansion-panel v-if="hasSettings">
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Contenuto</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<!-- Campi dichiarati nello schema `fields` del blocco (emailBlocks.js) -->
								<block-fields :fields="blockDef.fields" :value="settings" @input="onFieldsInput"></block-fields>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

					<!-- Spacing -->
					<v-expansion-panel>
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Spaziatura</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<label class="pp-label">Spazio interno (px)</label>
								<div class="d-flex" style="gap:6px">
									<v-text-field v-model="styles.paddingTop" placeholder="0" type="number" dense outlined persistent-hint hint="Sopra" @change="onStyleChange"></v-text-field>
									<v-text-field v-model="styles.paddingRight" placeholder="0" type="number" dense outlined persistent-hint hint="Destra" @change="onStyleChange"></v-text-field>
									<v-text-field v-model="styles.paddingBottom" placeholder="0" type="number" dense outlined persistent-hint hint="Sotto" @change="onStyleChange"></v-text-field>
									<v-text-field v-model="styles.paddingLeft" placeholder="0" type="number" dense outlined persistent-hint hint="Sinistra" @change="onStyleChange"></v-text-field>
								</div>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

					<!-- Typography -->
					<v-expansion-panel>
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Tipografia</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<div class="pp-row">
									<label class="pp-label">Font</label>
									<v-select v-model="styles.fontFamily" :items="fonts" placeholder="Predefinito" dense outlined hide-details @change="onStyleChange"></v-select>
								</div>
								<div class="pp-grid pp-row">
									<div>
										<label class="pp-label">Dimensione</label>
										<v-text-field v-model="styles.fontSize" dense outlined hide-details type="number" suffix="px" @change="onStyleChange"></v-text-field>
									</div>
									<div>
										<label class="pp-label">Peso</label>
										<v-select v-model="styles.fontWeight" :items="fontWeights" dense outlined hide-details @change="onStyleChange"></v-select>
									</div>
								</div>
								<color-field class="pp-row" :value="styles.color" label="Colore testo" clearable @input="setColor('color', $event)"></color-field>
								<div class="pp-row">
									<label class="pp-label">Allineamento</label>
									<v-btn-toggle v-model="styles.textAlign" dense @change="onStyleChange">
										<v-btn small value="left"><v-icon small>mdi-format-align-left</v-icon></v-btn>
										<v-btn small value="center"><v-icon small>mdi-format-align-center</v-icon></v-btn>
										<v-btn small value="right"><v-icon small>mdi-format-align-right</v-icon></v-btn>
										<v-btn small value="justify"><v-icon small>mdi-format-align-justify</v-icon></v-btn>
									</v-btn-toggle>
								</div>
								<div class="pp-grid">
									<div>
										<label class="pp-label">Altezza riga</label>
										<v-text-field v-model="styles.lineHeight" placeholder="es. 1.5" dense outlined hide-details @change="onStyleChange"></v-text-field>
									</div>
									<div>
										<label class="pp-label">Spaziatura lettere</label>
										<v-text-field v-model="styles.letterSpacing" dense outlined hide-details suffix="px" @change="onStyleChange"></v-text-field>
									</div>
								</div>
								<div class="pp-row mt-3 mb-0">
									<label class="pp-label">Maiuscole</label>
									<v-select v-model="styles.textTransform" :items="textTransforms" dense outlined hide-details @change="onStyleChange"></v-select>
								</div>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

					<!-- Tipografia del titolo: seconda scala, solo per i blocchi che rendono un titolo proprio -->
					<v-expansion-panel v-if="hasTitleStyles">
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Tipografia del titolo</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<div class="pp-hint pp-row">Vale solo per il titolo del blocco. Lasciato vuoto segue la tipografia qui sopra.</div>
								<div class="pp-grid pp-row">
									<div>
										<label class="pp-label">Dimensione</label>
										<v-text-field v-model="titleStyles.fontSize" dense outlined hide-details type="number" suffix="px" @change="onTitleStyleChange"></v-text-field>
									</div>
									<div>
										<label class="pp-label">Peso</label>
										<v-select v-model="titleStyles.fontWeight" :items="fontWeights" dense outlined hide-details @change="onTitleStyleChange"></v-select>
									</div>
									<div>
										<label class="pp-label">Altezza riga</label>
										<v-text-field v-model="titleStyles.lineHeight" placeholder="es. 1.2" dense outlined hide-details @change="onTitleStyleChange"></v-text-field>
									</div>
									<div>
										<label class="pp-label">Spaziatura lettere</label>
										<v-text-field v-model="titleStyles.letterSpacing" dense outlined hide-details suffix="px" @change="onTitleStyleChange"></v-text-field>
									</div>
								</div>
								<div class="pp-row">
									<label class="pp-label">Maiuscole</label>
									<v-select v-model="titleStyles.textTransform" :items="textTransforms" dense outlined hide-details @change="onTitleStyleChange"></v-select>
								</div>
								<color-field :value="titleStyles.color" label="Colore titolo" clearable @input="setTitleColor"></color-field>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

					<!-- Background -->
					<v-expansion-panel>
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Sfondo</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<!-- Senza colore il blocco mostra lo sfondo della sezione, o il bianco della mail -->
								<color-field :value="styles.backgroundColor" label="Colore sfondo" clearable placeholder="Nessuno (trasparente)" @input="setColor('backgroundColor', $event)"></color-field>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

					<!-- Borders -->
					<v-expansion-panel>
						<v-expansion-panel-header class="py-2 px-3">
							<span class="body-2 font-weight-medium">Bordi</span>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="px-3 pb-3">
								<div class="pp-grid pp-row">
									<div>
										<label class="pp-label">Spessore</label>
										<v-text-field v-model="styles.borderWidth" dense outlined hide-details type="number" suffix="px" @change="onStyleChange"></v-text-field>
									</div>
									<div>
										<label class="pp-label">Stile</label>
										<v-select v-model="styles.borderStyle" :items="borderStyles" dense outlined hide-details @change="onStyleChange"></v-select>
									</div>
								</div>
								<color-field class="pp-row" :value="styles.borderColor" label="Colore bordo" clearable placeholder="Predefinito #DDDDDD" @input="setColor('borderColor', $event)"></color-field>
								<label class="pp-label">Raggio angoli</label>
								<v-text-field v-model="styles.borderRadius" dense outlined hide-details type="number" suffix="px" @change="onStyleChange"></v-text-field>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>

				</v-expansion-panels>
			</div>
		</template>

		<!-- No selection state -->
		<template v-else>
			<div class="pa-4 text-center grey--text" style="margin-top:40%">
				<v-icon large color="grey lighten-1" class="mb-3">mdi-cursor-default-click</v-icon>
				<p class="body-2">Seleziona un blocco per modificarne le proprietà</p>
			</div>
		</template>
	</div>
</template>

<script>
import { findBlockDef, emailFonts } from './emailBlocks'
import BlockFields from './BlockFields'
import ColorField from './ColorField'

export default {
	name: 'properties-panel',

	components: { 'block-fields': BlockFields, 'color-field': ColorField },

	props: {
		block: { type: Object, default: null }
	},

	data() {
		return {
			openPanels: [0, 1, 2, 3, 4, 5],
			fonts: emailFonts,
			borderStyles: [
				{ text: 'Nessuno', value: 'none' },
				{ text: 'Continuo', value: 'solid' },
				{ text: 'Tratteggiato', value: 'dashed' },
				{ text: 'Puntinato', value: 'dotted' }
			],
			fontWeights: [
				{ text: 'Leggero', value: '300' },
				{ text: 'Normale', value: '400' },
				{ text: 'Medio', value: '500' },
				{ text: 'Semi-Grassetto', value: '600' },
				{ text: 'Grassetto', value: '700' }
			],
			textTransforms: [
				{ text: 'Come scritto', value: '' },
				{ text: 'TUTTO MAIUSCOLO', value: 'uppercase' },
				{ text: 'tutto minuscolo', value: 'lowercase' },
				{ text: 'Iniziali Maiuscole', value: 'capitalize' }
			],
			styles: {},
			titleStyles: {},
			settings: {}
		}
	},

	computed: {
		blockDef() {
			return this.block ? findBlockDef(this.block.type) : null
		},

		blockLabel() {
			if (!this.block) return ''
			return this.blockDef ? this.blockDef.label : this.block.type
		},

		blockIcon() {
			return this.blockDef && this.blockDef.icon ? this.blockDef.icon : 'mdi-cube-outline'
		},

		hasSettings() {
			return !!(this.blockDef && this.blockDef.fields && this.blockDef.fields.length)
		},

		// Blocco con render(): il content si rigenera da settings+styles, non si ritocca inline
		isRendered() {
			return !!(this.blockDef && this.blockDef.render)
		},

		hasTitleStyles() {
			return !!(this.block && this.block.titleStyles)
		},

		// Contenuto (se ha campi) + Spaziatura, Tipografia, Sfondo, Bordi + Tipografia del titolo (se c'e')
		panelCount() {
			return 4 + (this.hasSettings ? 1 : 0) + (this.hasTitleStyles ? 1 : 0)
		}
	},

	watch: {
		// Vuetify pota il modello quando un blocco mostra meno sezioni (i layout non hanno
		// "Contenuto") e non lo ripristina, e con un numero fisso di indici le sezioni oltre
		// restavano chiuse. Si riaprono tutte quelle del blocco nuovo, su pannelli ricreati
		// (:key) cosi' niente potatura a meta'. Non a ogni battuta: si guarda l'id, non l'oggetto
		'block.id': function() {
			var all = []
			for (var i = 0; i < this.panelCount; i++) all.push(i)
			this.openPanels = all
		},

		block: {
			handler(val) {
				if (val) {
					this.syncFromBlock()
				}
			},
			immediate: true,
			deep: true
		}
	},

	methods: {
		syncFromBlock() {
			if (!this.block) return

			var s = this.block.styles || {}
			this.styles = {
				paddingTop: this.parsePaddingPart(s.padding, 0) || s['padding-top'] || '',
				paddingRight: this.parsePaddingPart(s.padding, 1) || s['padding-right'] || '',
				paddingBottom: this.parsePaddingPart(s.padding, 2) || s['padding-bottom'] || '',
				paddingLeft: this.parsePaddingPart(s.padding, 3) || s['padding-left'] || '',
				fontFamily: s['font-family'] || '',
				fontSize: (s['font-size'] || '').replace('px', ''),
				fontWeight: s['font-weight'] || '',
				color: s.color || '',
				textAlign: s['text-align'] || '',
				lineHeight: s['line-height'] || '',
				backgroundColor: s['background-color'] || '',
				borderWidth: (s['border-width'] || '').replace('px', ''),
				borderStyle: s['border-style'] || 'none',
				borderColor: s['border-color'] || '',
				borderRadius: (s['border-radius'] || '').replace('px', ''),
				letterSpacing: (s['letter-spacing'] || '').replace('px', ''),
				textTransform: s['text-transform'] || ''
			}

			var t = this.block.titleStyles || {}
			this.titleStyles = {
				fontSize: (t['font-size'] || '').replace('px', ''),
				fontWeight: t['font-weight'] || '',
				lineHeight: t['line-height'] || '',
				letterSpacing: (t['letter-spacing'] || '').replace('px', ''),
				textTransform: t['text-transform'] || '',
				color: t.color || ''
			}

			this.settings = Object.assign({}, this.block.settings || {})
		},

		onTitleStyleChange() {
			var t = this.titleStyles
			var result = {}
			if (t.fontSize) result['font-size'] = t.fontSize + 'px'
			if (t.fontWeight) result['font-weight'] = t.fontWeight
			if (t.lineHeight) result['line-height'] = t.lineHeight
			if (t.letterSpacing) result['letter-spacing'] = t.letterSpacing + 'px'
			if (t.textTransform) result['text-transform'] = t.textTransform
			if (t.color) result.color = t.color

			this.$emit('title-style-change', { id: this.block.id, titleStyles: result })
		},

		setTitleColor(color) {
			this.titleStyles.color = color
			this.onTitleStyleChange()
		},

		// '' arriva da "Rimuovi": toglie anche lo stile inline dai tag interni
		setColor(key, color) {
			if (!color) return this.clearStyle(key)
			this.styles[key] = color
			this.onStyleChange()
		},

		parsePaddingPart(padding, index) {
			if (!padding) return ''
			var parts = padding.replace(/px/g, '').trim().split(/\s+/)
			if (parts.length === 1) return parts[0]
			if (parts.length === 2) return parts[index % 2]
			if (parts.length === 4) return parts[index]
			return parts[0]
		},

		onStyleChange() {
			this.applyStyles([])
		},

		applyStyles(removeProps) {
			var result = {}

			if (this.styles.paddingTop || this.styles.paddingRight || this.styles.paddingBottom || this.styles.paddingLeft) {
				var pt = (this.styles.paddingTop || '0') + 'px'
				var pr = (this.styles.paddingRight || '0') + 'px'
				var pb = (this.styles.paddingBottom || '0') + 'px'
				var pl = (this.styles.paddingLeft || '0') + 'px'
				result.padding = pt + ' ' + pr + ' ' + pb + ' ' + pl
			}

			if (this.styles.fontFamily) result['font-family'] = this.styles.fontFamily
			if (this.styles.fontSize) result['font-size'] = this.styles.fontSize + 'px'
			if (this.styles.fontWeight) result['font-weight'] = this.styles.fontWeight
			if (this.styles.color) result.color = this.styles.color
			if (this.styles.textAlign) result['text-align'] = this.styles.textAlign
			if (this.styles.lineHeight) result['line-height'] = this.styles.lineHeight
			if (this.styles.backgroundColor) result['background-color'] = this.styles.backgroundColor

			if (this.styles.borderWidth && this.styles.borderStyle && this.styles.borderStyle !== 'none') {
				result['border-width'] = this.styles.borderWidth + 'px'
				result['border-style'] = this.styles.borderStyle
				result['border-color'] = this.styles.borderColor || '#dddddd'
			}
			if (this.styles.borderRadius) result['border-radius'] = this.styles.borderRadius + 'px'
			if (this.styles.letterSpacing) result['letter-spacing'] = this.styles.letterSpacing + 'px'
			if (this.styles.textTransform) result['text-transform'] = this.styles.textTransform

			this.$emit('style-change', { id: this.block.id, styles: result })

			// Applica stili tipografia anche nell'HTML inline del blocco (i renderizzati li rigenera il contenitore)
			if (this.block.content && !this.isRendered) {
				var updated = this.applyInlineStyles(this.block.content, result, removeProps)
				if (updated !== this.block.content) {
					this.$emit('content-change', { id: this.block.id, content: updated })
				}
			}
		},

		applyInlineStyles: function(html, styles, removeProps) {
			var div = document.createElement('div')
			div.innerHTML = html

			// Stili da applicare agli elementi interni
			var typoStyles = {}
			if (styles['font-family']) typoStyles['font-family'] = styles['font-family']
			if (styles['font-size']) typoStyles['font-size'] = styles['font-size']
			if (styles['font-weight']) typoStyles['font-weight'] = styles['font-weight']
			if (styles.color) typoStyles.color = styles.color
			if (styles['text-align']) typoStyles['text-align'] = styles['text-align']
			if (styles['line-height']) typoStyles['line-height'] = styles['line-height']

			if (!Object.keys(typoStyles).length && !removeProps.length) return html

			// Solo i contenitori di testo (p, h1-h6, td): link e span mantengono i loro stili.
			// Sui titoli non si toccano dimensione e peso, sono cio' che li distingue dal testo.
			var targets = div.querySelectorAll('p, h1, h2, h3, h4, h5, h6, td')
			for (var i = 0; i < targets.length; i++) {
				var isHeading = /^H[1-6]$/.test(targets[i].tagName)
				for (var r = 0; r < removeProps.length; r++) {
					targets[i].style.removeProperty(removeProps[r])
				}
				for (var prop in typoStyles) {
					if (isHeading && (prop === 'font-size' || prop === 'font-weight')) continue
					targets[i].style[this.cssPropToCamel(prop)] = typoStyles[prop]
				}
			}

			// Se nessun target trovato, applica al primo figlio
			if (!targets.length && div.firstElementChild) {
				for (var p in typoStyles) {
					div.firstElementChild.style[this.cssPropToCamel(p)] = typoStyles[p]
				}
			}

			return div.innerHTML
		},

		// "Rimuovi" toglie anche la proprieta' inline dai tag interni, altrimenti
		// il canvas non cambierebbe (applyInlineStyles aggiunge, non toglie).
		clearStyle: function(key) {
			this.styles[key] = ''
			this.applyStyles([key === 'color' ? 'color' : key])
		},

		cssPropToCamel: function(prop) {
			return prop.replace(/-([a-z])/g, function(m, c) { return c.toUpperCase() })
		},

		onFieldsInput(settings) {
			this.settings = settings
			this.onSettingsChange()
		},

		onSettingsChange() {
			this.$emit('settings-change', { id: this.block.id, settings: Object.assign({}, this.settings) })
		}
	}
}
</script>

<style scoped>
.properties-panel {
	height: 100%;
	overflow-y: auto;
	background: #fff;
}

.properties-panel__scroll {
	overflow-y: auto;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
	padding: 0;
}

.v-expansion-panel-header {
	min-height: 44px !important;
}

.v-expansion-panel + .v-expansion-panel {
	border-top: 1px solid #eeeeee;
}
</style>

<!-- Non scoped: valgono anche dentro BlockFields, ImagePicker e ColorField, sempre sotto .properties-panel -->
<style>
.properties-panel .pp-label {
	display: block;
	margin-bottom: 4px;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	color: rgba(0, 0, 0, 0.72);
}

.properties-panel .pp-section {
	margin: 18px 0 10px;
	padding-top: 12px;
	border-top: 1px solid #eeeeee;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: rgba(0, 0, 0, 0.6);
}

.properties-panel .block-fields__row:first-child > .pp-section {
	margin-top: 0;
	padding-top: 0;
	border-top: 0;
}

.properties-panel .pp-hint {
	margin: 0;
	padding-left: 8px;
	border-left: 2px solid #cfd8dc;
	font-size: 12px;
	line-height: 1.45;
	color: rgba(0, 0, 0, 0.62);
}

.properties-panel .pp-row {
	margin-bottom: 12px;
}

.properties-panel .pp-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px 8px;
}

/* Icona dentro il campo (social del footer): senza etichetta Vuetify la lascia in alto */
.properties-panel .v-text-field--outlined.v-input--dense .v-input__prepend-inner {
	align-self: center;
	margin-top: 0 !important;
}
</style>
